import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { showToastFromSnackbar } from '@/utils/showToast';
import socketService from '@/services/socketService';
import { queryClient } from '@/lib/queryClient';
import { useLocales } from '@/locales';
import { useErrorDialogDispatch } from '@/stores/errorDialogStore';
import { useUserStore } from '@/stores/userStore';
import { useSessionStore } from '@/stores/sessionStore';
import { useUserMe } from '@/hooks/api/useUsers';
import { useDocuments, DOCUMENTS_KEYS } from '@/hooks/api/useDocuments';
import {
  useCertifications,
  CERTIFICATIONS_KEYS,
} from '@/hooks/api/useCertifications';
import { useCodeRequests, CODES_KEYS } from '@/hooks/api/useCodes';
import {
  useNotifications,
  NOTIFICATIONS_KEYS,
} from '@/hooks/api/useNotifications';
import { usePortOffices } from '@/hooks/api/useUtility';
import Backdrop from '@/components/ui/feedback/Backdrop';

// Socket payload `application.type` → query key to invalidate
const COLLECTION_KEYS = {
  document: DOCUMENTS_KEYS.all,
  certificate: CERTIFICATIONS_KEYS.all,
  codetnved: CODES_KEYS.all,
};

/**
 * AppWrapper - Initializes the user app view.
 * Collections come from React Query; the socket only invalidates queries.
 */
export function AppWrapper({ children }) {
  const navigate = useNavigate();

  const { translate } = useLocales();
  const errorDispatch = useErrorDialogDispatch();

  const user = useUserStore(state => state.user);
  const setUser = useUserStore(state => state.setUser);
  const setAppView = useSessionStore(state => state.setAppView);

  const meQuery = useUserMe();
  const documentsQuery = useDocuments();
  const certificationsQuery = useCertifications();
  const codesQuery = useCodeRequests();
  const notificationsQuery = useNotifications();
  const officesQuery = usePortOffices();

  const ready =
    meQuery.isSuccess &&
    documentsQuery.isSuccess &&
    certificationsQuery.isSuccess &&
    codesQuery.isSuccess &&
    notificationsQuery.isSuccess &&
    officesQuery.isSuccess;

  const failed =
    meQuery.isError ||
    documentsQuery.isError ||
    certificationsQuery.isError ||
    codesQuery.isError ||
    notificationsQuery.isError ||
    officesQuery.isError;

  // Refresh the stored user identity from the server profile
  useEffect(() => {
    const details = meQuery.data?.data;
    if (!details) return;
    setUser({
      id: details._id,
      role: 'user',
      isVerified: false,
      token: user.token,
      details,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.data]);

  // Preserve the sessionStore appView flag for existing consumers
  useEffect(() => {
    if (ready) setAppView(true);
  }, [ready, setAppView]);

  useEffect(() => {
    if (!failed) return;
    errorDispatch({
      type: 'OPEN_ERROR_DIALOG',
      errorMsg: translate('systemError'),
    });
    navigate('/auth/user');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  // Socket lifecycle: connect once per user, refetch on server notifications
  useEffect(() => {
    if (!user?.id) return undefined;

    socketService.connect(user.id, user.socketUUID);

    const handleSocketNotification = ({ snackbar, application }) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });

      const collectionKey = COLLECTION_KEYS[application.type];
      if (collectionKey) {
        queryClient.invalidateQueries({ queryKey: collectionKey });
      }

      showToastFromSnackbar(
        `${translate(snackbar.messageHeader)} ${snackbar.uuid} : ${translate(
          snackbar.messageBody,
        )}`,
        { variant: snackbar.type },
      );
    };

    socketService.on('getNotify', handleSocketNotification);

    return () => {
      socketService.off('getNotify', handleSocketNotification);
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (ready) {
    return children;
  }

  return <Backdrop open={true} background />;
}

AppWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

// Backwards compatibility alias
export const AppViewProvider = AppWrapper;

export default AppWrapper;
